import React from 'react';
import { PSConversationArea } from '@/components/agent/PSConversationArea';
import { PSSessionFeedback } from '@/components/agent/PSSessionFeedback';
import { usePSAgentCall } from '@/hooks/usePSAgentCall';
import { useOpenAIRealtimeCall } from '@/hooks/useOpenAIRealtimeCall';
import { useLivekitPSAgentCall } from '@/hooks/useLivekitPSAgentCall';
import { useCoachStore } from '@/store/coach';
import { Box, Typography } from '@mui/material';
import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CallState,
  LoadingState,
  EngineType,
  PSAgentCallReturn,
} from '@/types/ps-common';

export function PSAgent() {
  const coachStore = useCoachStore();
  const [hasStartedCall, setHasStartedCall] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [searchParams] = useSearchParams();

  // Parse version from URL params, default to 2
  const version = useMemo(() => {
    const versionParam = searchParams.get('version');
    if (versionParam === '1') return 1;
    if (versionParam === '3') return 3;
    if (versionParam === '4') return 4;
    if (versionParam === '5') return 5;
    if (versionParam === '6') return 6;
    if (versionParam === '7') return 7;
    if (versionParam === '8') return 8;
    return 2;
  }, [searchParams]);

  // Parse lang from URL params, default to 'en'
  const lang = useMemo(() => {
    return searchParams.get('lang') || 'en';
  }, [searchParams]);

  // Parse engine from URL params, default to 'elevenlabs'
  const engine = useMemo(() => {
    const engineParam = searchParams.get('engine');
    if (engineParam === 'realtime') return EngineType.OPENAI_REALTIME;
    if (engineParam === 'livekit') return EngineType.LIVEKIT;
    return EngineType.ELEVENLABS;
  }, [searchParams]);

  // Parse provider from URL params, default to 'openai'
  const provider = useMemo(() => {
    return searchParams.get('provider') || 'openai';
  }, [searchParams]);

  // Parse voice from URL params (optional - for Cartesia voice selection)
  const voice = useMemo(() => {
    return searchParams.get('voice') || undefined;
  }, [searchParams]);

  // Conditional hook selection based on engine
  const useSelectedAgentCall = (() => {
    if (engine === EngineType.OPENAI_REALTIME) return useOpenAIRealtimeCall;
    if (engine === EngineType.LIVEKIT) return useLivekitPSAgentCall;
    return usePSAgentCall;
  })();

  const agentCallResult: PSAgentCallReturn = useSelectedAgentCall({
    userId: coachStore.userId,
    version,
    lang,
    engine,
    provider,
    voice,
    onCallEnded: () => {
      // Show feedback screen instead of just staying on same page
      setShowFeedback(true);
    },
  });

  const {
    loadingState,
    error,
    callState,
    sessionId,
    conversationId,
    agentInfo,
    callDuration,
    transcript,
    isSpeaking,
    currentQuestion,
    surveyProgress,
    handleAnswerCall,
    handleEndCall,
    formatTime,
  } = agentCallResult;

  const handleStartCall = () => {
    setHasStartedCall(true);
    handleAnswerCall();
  };

  const handleNewCall = () => {
    window.location.reload();
  };

  const handleStartNewSession = () => {
    setShowFeedback(false);
    setHasStartedCall(false);
    window.location.reload();
  };

  // Home screen - before call
  if (callState === CallState.INCOMING && !hasStartedCall) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          bgcolor: '#f5f5f5',
        }}
      >
        <Box
          sx={{
            textAlign: 'center',
            maxWidth: 600,
          }}
        >
          {/* Avatar */}
          <Box
            sx={{
              width: 160,
              height: 160,
              borderRadius: '50%',
              overflow: 'hidden',
              margin: '0 auto',
              mb: 3,
              boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
            }}
          >
            <img
              src={agentInfo.profile.avatar}
              alt="Post-Sales Survey"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>

          {/* Title */}
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
            {agentInfo.profile.name}
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
            {version === 8
              ? '保誠人壽 續期保費授權扣款確認'
              : version === 7
              ? '保誠人壽 新契約承保前電話訪問'
              : version === 6
              ? '日本損保 アウトバウンドコール'
              : version === 5
              ? '휴먼생명보험 아웃바운드 콜'
              : version === 4
                ? 'Outbound Call from Prudential Taiwan'
                : 'Post-Sales Survey Call from Prudential Singapore'}
          </Typography>

          {/* Start Call Button */}
          <Box
            onClick={handleStartCall}
            sx={{
              px: 6,
              py: 3,
              bgcolor: '#0064CC',
              color: 'white',
              borderRadius: 3,
              cursor: 'pointer',
              fontSize: '18px',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(0, 100, 204, 0.3)',
              transition: 'all 0.2s',
              '&:hover': {
                bgcolor: '#0056b3',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 20px rgba(0, 100, 204, 0.4)',
              },
            }}
          >
            {version === 7 || version === 8 ? '開始通話' : version === 6 ? '通話開始' : version === 5 ? '통화 시작' : version === 4 ? 'Start Call' : 'Start Survey Call'}
          </Box>

          {/* Info */}
          <Typography
            variant="caption"
            sx={{ mt: 3, color: '#666', display: 'block' }}
          >
            {version === 8
              ? '這是一通確認您續期保費授權扣款的電話，全程錄音'
              : version === 7
              ? '這是一通新契約承保前的電話訪問，全程錄音'
              : version === 6
              ? '保障内容の点検および相談日程調整のための電話です'
              : version === 5
              ? '보장 내용 점검 및 상담 일정을 위한 전화입니다'
              : version === 4
                ? 'A quick check-in to understand your protection needs'
                : 'This is a quality assurance survey to ensure you received excellent service'}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Connecting screen
  if (hasStartedCall && callState === CallState.INCOMING) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box
            sx={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              overflow: 'hidden',
              margin: '0 auto',
              mb: 3,
            }}
          >
            <img
              src={agentInfo.profile.avatar}
              alt="Post-Sales Survey"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          </Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {version === 7 || version === 8 ? '連線中...' : version === 6 ? '接続中...' : version === 5 ? '연결 중...' : 'Connecting...'}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            {[0, 0.5, 1].map((delay) => (
              <Box
                key={delay}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#0064CC',
                  animation: 'pulse 1.5s infinite',
                  animationDelay: `${delay}s`,
                  '@keyframes pulse': {
                    '0%, 60%': { opacity: 0.3 },
                    '30%': { opacity: 1 },
                  },
                }}
              />
            ))}
          </Box>
        </Box>
      </Box>
    );
  }

  // Active call screen
  if (callState === CallState.ACTIVE) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          bgcolor: '#f5f5f5',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            bgcolor: 'white',
            borderBottom: '1px solid #e0e0e0',
            p: 2,
            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              maxWidth: 1200,
              margin: '0 auto',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  overflow: 'hidden',
                }}
              >
                <img
                  src={agentInfo.profile.avatar}
                  alt="Post-Sales Survey"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                />
              </Box>
              <Box>
                <Box sx={{ fontWeight: 600, color: '#333' }}>
                  {agentInfo.profile.name}
                </Box>
                <Box sx={{ fontSize: '14px', color: '#666' }}>
                  {version === 7 || version === 8 ? `電話訪問 - ${formatTime(callDuration)}` : version === 6 ? `相談通話 - ${formatTime(callDuration)}` : version === 5 ? `상담 통화 - ${formatTime(callDuration)}` : `Survey Call - ${formatTime(callDuration)}`}
                </Box>
              </Box>
            </Box>


            <Box
              onClick={handleEndCall}
              sx={{
                px: 3,
                py: 1.5,
                bgcolor: '#DC3545',
                color: 'white',
                borderRadius: 2,
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 600,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: '#B02A37',
                },
              }}
            >
              {version === 7 || version === 8 ? '結束通話' : version === 6 ? '通話終了' : version === 5 ? '통화 종료' : 'End Call'}
            </Box>
          </Box>
        </Box>

        {/* Main content */}
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Survey sidebar */}
          <Box
            sx={{
              width: 320,
              bgcolor: 'white',
              borderRight: '1px solid #e0e0e0',
              p: 3,
              overflowY: 'auto',
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
              {version === 7 || version === 8 ? '通話資訊' : version === 6 ? '通話情報' : version === 5 ? '통화 정보' : version === 4 ? 'Call Information' : 'Survey Information'}
            </Typography>

            {/* Current Question - hidden for v5/v6 (non-English UI) and v7/v8 (no SG survey topic) */}
            {currentQuestion && version !== 5 && version !== 6 && version !== 7 && version !== 8 && (
              <Box
                sx={{
                  mb: 3,
                  p: 2,
                  bgcolor: '#f0f7ff',
                  borderRadius: 2,
                  border: '1px solid #d0e7ff',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: '#0064CC', fontWeight: 600, mb: 1 }}
                >
                  {version >= 4 ? 'Current Topic' : 'Current Question'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#333' }}>
                  {currentQuestion}
                </Typography>
              </Box>
            )}

            {/* Details section - different for V4 */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {version === 7 || version === 8 ? '詳細資訊' : version === 6 ? '詳細情報' : version === 5 ? '상세 정보' : version === 4 ? 'Call Details' : 'Policy Details'}
              </Typography>
              <Box sx={{ p: 2, bgcolor: '#f8f9fa', borderRadius: 1 }}>
                {version === 8 ? (
                  <>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      公司：保誠人壽台灣
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      類型：續期保費電話確認
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      目的：授權扣款確認
                    </Typography>
                  </>
                ) : version === 7 ? (
                  <>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      公司：保誠人壽台灣
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      類型：新契約承保前電話訪問
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      目的：承保前確認
                    </Typography>
                  </>
                ) : version === 6 ? (
                  <>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      会社: 日本損保
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      顧客: 田中浩様
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                      担当: 鈴木大輝
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                      目的: 保険見直し相談予約
                    </Typography>
                  </>
                ) : version === 5 ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      통화 유형: 아웃바운드 콜
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      시장: 한국
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      목적: 보장 리뷰 상담 예약
                    </Typography>
                  </>
                ) : version === 4 ? (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      Call Type: Outbound Cold Call
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      Market: Taiwan
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      Purpose: Lead Generation
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      Policy Type: Investment-Linked
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      Agent: Your Financial Consultant
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#666', display: 'block' }}
                    >
                      Purchase Date: Recent
                    </Typography>
                  </>
                )}
              </Box>
            </Box>

            {/* Purpose section */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                {version === 7 || version === 8 ? '訪問目的' : version === 6 ? 'アウトバウンドコール目的' : version === 5 ? '아웃바운드 콜 목적' : version === 4 ? 'Call Purpose' : 'Survey Purpose'}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: '#666', lineHeight: 1.5 }}
              >
                {version === 8
                  ? '這通電話是為了確認您續期保費的授權扣款資料，包括身分、親簽、扣款帳戶或信用卡資訊及應繳保費金額，全程錄音。'
                  : version === 7
                  ? '這通電話是新契約承保前的電話訪問，逐項確認您的身分、親簽、商品適合度與風險、保費來源與負擔及契約撤銷權，全程錄音。'
                  : version === 6
                  ? 'この電話は、お客様の現在の保険保障状況を確認し、アドバイザー鈴木大輝の相談日程を調整するためのものです。'
                  : version === 5
                  ? '이 전화는 고객님의 현재 보장 상태를 확인하고, 어드바이저 김동민의 상담 일정을 잡기 위한 것입니다.'
                  : version === 4
                    ? 'This is a check-in call to understand the customer\'s current protection situation and offer a 15-minute life protection review if appropriate.'
                    : 'This survey ensures that you received comprehensive information about your policy and that all regulatory requirements were met during your purchase.'}
              </Typography>
            </Box>
          </Box>

          {/* Conversation area */}
          <Box sx={{ flex: 1, overflow: 'hidden' }}>
            <PSConversationArea
              transcript={transcript}
              isSpeaking={isSpeaking}
              currentQuestion={currentQuestion}
              agentName={agentInfo.profile.name}
              version={version}
            />
          </Box>
        </Box>
      </Box>
    );
  }

  // Post-session feedback screen
  if (showFeedback && callState === CallState.ENDED) {
    return (
      <PSSessionFeedback
        sessionId={sessionId}
        conversationId={conversationId}
        callDuration={callDuration}
        transcript={transcript}
        formatTime={formatTime}
        onStartNewSession={handleStartNewSession}
        version={version}
        agentName={agentInfo.profile.name}
      />
    );
  }

  // Call ended screen (fallback - shouldn't be reached with new flow)
  if (callState === CallState.ENDED) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          bgcolor: '#f5f5f5',
        }}
      >
        <Box sx={{ fontSize: '48px', mb: 3, color: '#28a745' }}>✓</Box>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
          {version === 7 || version === 8 ? '電話訪問完成' : version === 6 ? '通話完了' : version === 5 ? '통화 완료' : 'Survey Completed'}
        </Typography>
        <Typography variant="body1" sx={{ color: '#666', mb: 4 }}>
          {version === 7 || version === 8 ? `通話時間: ${formatTime(callDuration)}` : version === 6 ? `通話時間: ${formatTime(callDuration)}` : version === 5 ? `통화 시간: ${formatTime(callDuration)}` : `Call Duration: ${formatTime(callDuration)}`}
        </Typography>

        <Box sx={{ textAlign: 'center' }}>
          <Box
            onClick={handleNewCall}
            sx={{
              px: 4,
              py: 2,
              bgcolor: '#0064CC',
              color: 'white',
              borderRadius: 2,
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 600,
              mb: 2,
              '&:hover': {
                bgcolor: '#0056b3',
              },
            }}
          >
            {version === 7 || version === 8 ? '開始新通話' : version === 6 ? '新規通話開始' : version === 5 ? '새 통화 시작' : 'Start New Survey'}
          </Box>

          <Typography variant="caption" sx={{ color: '#666' }}>
            {version === 7 || version === 8 ? '感謝您接受這次電話訪問' : version === 6 ? '通話にご参加いただきありがとうございます' : version === 5 ? '통화에 참여해 주셔서 감사합니다' : 'Thank you for completing the survey'}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Error screen
  if (error && loadingState === LoadingState.ERROR) {
    return (
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          bgcolor: '#f5f5f5',
        }}
      >
        <Box sx={{ fontSize: '48px', mb: 3 }}>⚠️</Box>
        <Typography variant="h6" sx={{ color: '#DC3545', mb: 3 }}>
          {version === 7 || version === 8 ? '連線錯誤' : version === 6 ? '接続エラー' : version === 5 ? '연결 오류' : 'Connection Error'}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: '#666', mb: 3, textAlign: 'center' }}
        >
          {version === 7 || version === 8 ? '很抱歉發生技術問題，請再試一次。' : version === 6 ? '技術的な問題が発生しました。再度お試しください。' : version === 5 ? '기술적 문제가 발생했습니다. 다시 시도해 주세요.' : 'We apologize for the technical issue. Please try again.'}
        </Typography>
        <Box
          onClick={handleNewCall}
          sx={{
            px: 4,
            py: 2,
            bgcolor: '#0064CC',
            color: 'white',
            borderRadius: 2,
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600,
            '&:hover': {
              bgcolor: '#0056b3',
            },
          }}
        >
          {version === 7 || version === 8 ? '重試' : version === 6 ? '再試行' : version === 5 ? '다시 시도' : 'Retry'}
        </Box>
      </Box>
    );
  }

  return null;
}
